/**
 * PDP sticky add-to-cart — lifecycle controller with observer rebind + layout visibility.
 */
(function () {
  'use strict';

  var controllers = new WeakMap();
  var globalBound = false;
  var booted = false;

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function isCompactStickyLayoutViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  var STICKY_MOBILE_LAYOUT_PROPS = {
    info: ['display'],
    title: ['display'],
    main: ['display', 'grid-template-columns', 'align-items', 'gap', 'width'],
    btn: ['width', 'min-width', 'flex'],
  };

  function clearStickyMobileLayout(sticky) {
    if (!sticky) return;

    sticky.classList.remove('pdp-sticky-atc--mobile-rich');

    var info = qs('.pdp-sticky-atc__info', sticky);
    var title = qs('.pdp-sticky-atc__title', sticky);
    var main = qs('.pdp-sticky-atc__main', sticky);
    var btn = qs('[data-pdp-sticky-submit]', sticky);
    var nodes = [info, title, main, btn];
    var propsByNode = [STICKY_MOBILE_LAYOUT_PROPS.info, STICKY_MOBILE_LAYOUT_PROPS.title, STICKY_MOBILE_LAYOUT_PROPS.main, STICKY_MOBILE_LAYOUT_PROPS.btn];

    nodes.forEach(function (node, index) {
      if (!node) return;
      propsByNode[index].forEach(function (prop) {
        node.style.removeProperty(prop);
      });
    });
  }

  /** Mobile framework CSS collapses to button-only; restore title + price row when visible. */
  function applyStickyMobileLayout(sticky, show) {
    if (!sticky) return;

    if (!show || !isCompactStickyLayoutViewport()) {
      clearStickyMobileLayout(sticky);
      return;
    }

    var info = qs('.pdp-sticky-atc__info', sticky);
    var title = qs('.pdp-sticky-atc__title', sticky);
    var main = qs('.pdp-sticky-atc__main', sticky);
    var btn = qs('[data-pdp-sticky-submit]', sticky);

    sticky.classList.add('pdp-sticky-atc--mobile-rich');

    if (info) info.style.setProperty('display', 'block');
    if (title) title.style.setProperty('display', 'block');
    if (main) {
      main.style.setProperty('display', 'grid');
      main.style.setProperty('grid-template-columns', 'minmax(0, 1fr) auto');
      main.style.setProperty('align-items', 'center');
      main.style.setProperty('gap', '0.625rem');
      main.style.setProperty('width', '100%');
    }
    if (btn) {
      btn.style.setProperty('width', 'auto');
      btn.style.setProperty('min-width', '11rem');
      btn.style.setProperty('flex', 'none');
    }
  }

  function isDesignMode() {
    return !!(window.Shopify && Shopify.designMode);
  }

  function isConnected(node) {
    return !!(node && node.isConnected);
  }

  function isStickyEnabledForViewport(sticky) {
    if (!sticky || sticky.classList.contains('pdp-sticky-atc--hidden')) return false;
    if (sticky.hasAttribute('hidden')) return false;
    if (isMobileViewport()) return sticky.dataset.stickyMobile !== 'false';
    return sticky.dataset.stickyDesktop !== 'false';
  }

  function getHero() {
    return qs('[data-pdp-hero]', document) || qs('.pdp-hero', document);
  }

  function getSticky() {
    return qs('[data-pdp-sticky-atc]', document);
  }

  /** Purchase module — source of truth for sticky visibility (not the button alone). */
  function getHeroPurchaseAnchor(hero) {
    if (!hero) return null;
    return qs('[data-pdp-purchase]', hero) || qs('[data-pdp-hero-actions]', hero);
  }

  function getFooterSection() {
    return qs('[data-pdp-type="cta"]', document) || qs('.pdp-final-cta', document);
  }

  function getViewportHeight() {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  function isFooterSectionInView(section) {
    if (!isConnected(section)) return false;
    var rect = section.getBoundingClientRect();
    var vh = getViewportHeight();
    return rect.top < vh * 0.82 && rect.bottom > vh * 0.18;
  }

  function isPurchasePastViewport(anchor) {
    if (!isConnected(anchor)) return false;
    var rect = anchor.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom <= 0;
  }

  function isPurchaseInViewport(anchor) {
    if (!isConnected(anchor)) return false;
    var rect = anchor.getBoundingClientRect();
    var vh = getViewportHeight();
    return rect.width > 0 && rect.height > 0 && rect.top < vh && rect.bottom > 0;
  }

  function syncStickyFromHero(sticky, hero) {
    if (!sticky || !hero) return;

    var heroStyle = getComputedStyle(hero);
    ['--pdp-accent', '--pdp-accent-hover', '--pdp-text', '--pdp-text-muted'].forEach(function (prop) {
      var val = heroStyle.getPropertyValue(prop).trim();
      if (val) sticky.style.setProperty(prop, val);
    });

    var heroBtn = qs('[data-cro-cta="primary-atc"]', hero);
    var stickyBtn = qs('[data-pdp-sticky-submit]', sticky);
    if (heroBtn && stickyBtn && sticky.dataset.matchHeroCta !== 'false') {
      stickyBtn.textContent = heroBtn.textContent.trim();
    }

    if (stickyBtn && heroBtn) {
      var heroDisabled = heroBtn.disabled || heroBtn.getAttribute('aria-disabled') === 'true';
      stickyBtn.disabled = heroDisabled;
      stickyBtn.setAttribute('aria-disabled', heroDisabled ? 'true' : 'false');
      stickyBtn.classList.toggle('pdp-btn--sold-out', heroDisabled);
    }

    var heroPrice = qs('[data-pdp-price]', hero);
    var stickyPrice = qs('[data-pdp-sticky-price]', sticky);
    if (heroPrice && stickyPrice) stickyPrice.innerHTML = heroPrice.innerHTML;

    var heroTitle = qs('[data-pdp-hero-title]', hero);
    var stickyTitle = qs('[data-pdp-sticky-title]', sticky);
    if (heroTitle && stickyTitle) stickyTitle.textContent = heroTitle.textContent.trim();
  }

  function syncStickyHeight(sticky) {
    if (!sticky || !sticky.classList.contains('is-visible')) return;
    document.documentElement.style.setProperty('--pdp-sticky-height', sticky.offsetHeight + 'px');
  }

  function scheduleStickyHeightSync(sticky) {
    if (!sticky || !sticky.classList.contains('is-visible')) return;

    requestAnimationFrame(function () {
      syncStickyHeight(sticky);
      requestAnimationFrame(function () {
        syncStickyHeight(sticky);
      });
    });
  }

  function StickyController(sticky) {
    this.sticky = sticky;
    this.ac = new AbortController();
    this.hero = null;
    this.heroPurchaseAnchor = null;
    this.footerSection = null;
    this.heroObserver = null;
    this.footerObserver = null;
    this.resizeObserver = null;
    this.pendingVisibility = false;
    this.pendingRebind = false;
    this.footerVisible = false;
  }

  StickyController.prototype.signal = function () {
    return this.ac.signal;
  };

  StickyController.prototype.resolveNodes = function () {
    this.hero = getHero();
    this.heroPurchaseAnchor = getHeroPurchaseAnchor(this.hero);
    this.footerSection = getFooterSection();
    return isConnected(this.sticky) && isConnected(this.hero) && isConnected(this.heroPurchaseAnchor);
  };

  StickyController.prototype.teardownObservers = function () {
    if (this.heroObserver) {
      this.heroObserver.disconnect();
      this.heroObserver = null;
    }
    if (this.footerObserver) {
      this.footerObserver.disconnect();
      this.footerObserver = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  };

  StickyController.prototype.heroObserverOptions = function () {
    return {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.01, 0.05, 0.1, 0.25, 0.5],
    };
  };

  StickyController.prototype.footerObserverOptions = function () {
    return {
      root: null,
      rootMargin: '0px 0px -12% 0px',
      threshold: [0, 0.01],
    };
  };

  StickyController.prototype.bindObservers = function () {
    this.teardownObservers();
    if (!this.resolveNodes()) return false;

    var self = this;

    this.heroObserver = new IntersectionObserver(function () {
      self.scheduleVisibilityUpdate();
    }, this.heroObserverOptions());
    this.heroObserver.observe(this.heroPurchaseAnchor);

    if (this.footerSection) {
      this.footerObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === self.footerSection) {
            self.footerVisible = entry.isIntersecting;
          }
        });
        self.scheduleVisibilityUpdate();
      }, this.footerObserverOptions());
      this.footerObserver.observe(this.footerSection);
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () {
        self.scheduleRebind();
      });
      this.resizeObserver.observe(this.heroPurchaseAnchor);
      var purchaseOptions = qs('[data-pdp-purchase-options]', this.hero);
      if (purchaseOptions) this.resizeObserver.observe(purchaseOptions);
      if (this.footerSection) this.resizeObserver.observe(this.footerSection);
    }

    return true;
  };

  StickyController.prototype.computeShouldShow = function () {
    if (!isStickyEnabledForViewport(this.sticky)) return false;
    if (!this.resolveNodes()) return false;

    var footerInView = this.footerVisible || isFooterSectionInView(this.footerSection);
    if (footerInView) return false;

    /* Hide while above or within the purchase module; show only after it has scrolled past. */
    if (isPurchaseInViewport(this.heroPurchaseAnchor)) return false;
    if (!isPurchasePastViewport(this.heroPurchaseAnchor)) return false;

    return true;
  };

  StickyController.prototype.applyVisibility = function () {
    if (!isConnected(this.sticky)) return;

    var show = this.computeShouldShow();
    this.sticky.classList.toggle('is-visible', show);
    document.body.classList.toggle('pdp-sticky-active', show);
    applyStickyMobileLayout(this.sticky, show);

    if (show) scheduleStickyHeightSync(this.sticky);
    else {
      document.body.classList.remove('pdp-sticky-active');
      clearStickyMobileLayout(this.sticky);
    }
  };

  StickyController.prototype.scheduleVisibilityUpdate = function () {
    if (this.pendingVisibility) return;
    this.pendingVisibility = true;
    var self = this;
    requestAnimationFrame(function () {
      self.pendingVisibility = false;
      if (!isConnected(self.sticky)) return;
      if (!self.heroPurchaseAnchor || !self.heroPurchaseAnchor.isConnected) {
        self.rebind();
        return;
      }
      syncStickyFromHero(self.sticky, self.hero);
      self.applyVisibility();
    });
  };

  StickyController.prototype.scheduleRebind = function () {
    if (this.pendingRebind) return;
    this.pendingRebind = true;
    var self = this;
    requestAnimationFrame(function () {
      self.pendingRebind = false;
      self.rebind();
    });
  };

  StickyController.prototype.rebind = function () {
    if (!isConnected(this.sticky)) return;
    syncStickyFromHero(this.sticky, this.hero);
    this.bindObservers();
    this.applyVisibility();
  };

  StickyController.prototype.bindSubmit = function () {
    var stickyBtn = qs('[data-pdp-sticky-submit]', this.sticky);
    if (!stickyBtn || stickyBtn.dataset.pdpStickySubmitBound === 'true') return;

    stickyBtn.dataset.pdpStickySubmitBound = 'true';
    var self = this;

    stickyBtn.addEventListener(
      'click',
      function () {
        if (stickyBtn.disabled || stickyBtn.getAttribute('aria-disabled') === 'true') return;
        var hero = getHero();
        var form = qs('#pdp-product-form', hero) || qs('form[action*="/cart/add"]', hero);
        if (!form) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else {
          var submit = qs('[type="submit"]', form);
          if (submit) submit.click();
        }
      },
      { signal: self.signal() }
    );
  };

  StickyController.prototype.bindEvents = function () {
    var self = this;
    var signal = this.signal();

    var onScroll = function () {
      self.scheduleVisibilityUpdate();
    };

    window.addEventListener('scroll', onScroll, { passive: true, capture: true, signal: signal });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true, signal: signal });

    document.addEventListener('pdp:purchase-option-change', function () {
      self.scheduleRebind();
    }, { signal: signal });

    document.addEventListener('pdp:purchase-option-slots-change', function () {
      self.scheduleRebind();
    }, { signal: signal });

    var heroForm = qs('#pdp-product-form', this.hero) || qs('form.product-form', this.hero);
    if (heroForm) {
      heroForm.addEventListener('change', function () {
        syncStickyFromHero(self.sticky, self.hero);
      }, { signal: signal });
    }
  };

  StickyController.prototype.mount = function () {
    if (!this.resolveNodes()) return false;

    this.sticky.dataset.pdpStickyInit = 'true';
    syncStickyFromHero(this.sticky, this.hero);
    this.bindSubmit();
    this.bindEvents();

    if (!this.bindObservers()) return false;

    this.applyVisibility();

    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.rebind();
      });
    });

    return true;
  };

  StickyController.prototype.destroy = function () {
    var sticky = this.sticky;
    var stickyBtn = sticky ? qs('[data-pdp-sticky-submit]', sticky) : null;

    this.ac.abort();
    this.teardownObservers();

    if (isConnected(sticky)) {
      delete sticky.dataset.pdpStickyInit;
      sticky.classList.remove('is-visible');
      clearStickyMobileLayout(sticky);
      delete sticky._pdpRefreshStickyState;
    }

    document.body.classList.remove('pdp-sticky-active');
    controllers.delete(sticky);

    if (stickyBtn) delete stickyBtn.dataset.pdpStickySubmitBound;
  };

  function destroyController(sticky) {
    if (!sticky) return;
    var ctrl = controllers.get(sticky);
    if (ctrl) {
      ctrl.destroy();
      return;
    }
    delete sticky.dataset.pdpStickyInit;
    delete sticky._pdpRefreshStickyState;
    sticky.classList.remove('is-visible');
    document.body.classList.remove('pdp-sticky-active');
  }

  function mountController(sticky) {
    if (!sticky) return false;

    if (sticky.classList.contains('pdp-sticky-atc--hidden') || sticky.hasAttribute('hidden')) {
      destroyController(sticky);
      return false;
    }

    destroyController(sticky);

    var ctrl = new StickyController(sticky);
    controllers.set(sticky, ctrl);
    sticky._pdpRefreshStickyState = function () {
      ctrl.rebind();
    };

    if (!ctrl.mount()) {
      ctrl.destroy();
      return false;
    }

    return true;
  }

  function isMounted() {
    var sticky = getSticky();
    return !!(sticky && controllers.has(sticky) && sticky.dataset.pdpStickyInit === 'true');
  }

  function mountWhenReady(attempt) {
    var sticky = getSticky();
    if (!sticky) return;

    var hero = getHero();
    var anchor = getHeroPurchaseAnchor(hero);
    var ready = isConnected(sticky) && isConnected(hero) && isConnected(anchor);

    if (!ready && attempt < 80) {
      requestAnimationFrame(function () {
        mountWhenReady(attempt + 1);
      });
      return;
    }

    if (!ready && attempt < 120) {
      setTimeout(function () {
        mountWhenReady(attempt + 1);
      }, 50);
      return;
    }

    mountController(sticky);
  }

  function remountAll() {
    var sticky = getSticky();
    if (!sticky) {
      document.body.classList.remove('pdp-sticky-active');
      return;
    }
    mountWhenReady(0);
  }

  function refreshStickyAtc() {
    var sticky = getSticky();
    if (!sticky) return;
    var ctrl = controllers.get(sticky);
    if (ctrl) {
      ctrl.rebind();
      return;
    }
    mountWhenReady(0);
  }

  function bindGlobalEvents() {
    if (globalBound) return;
    globalBound = true;

    var resizePending = false;
    window.addEventListener('resize', function () {
      if (resizePending) return;
      resizePending = true;
      requestAnimationFrame(function () {
        resizePending = false;
        refreshStickyAtc();
      });
    }, { passive: true });

    if (window.matchMedia) {
      var mobileQuery = window.matchMedia('(max-width: 989px)');
      var onBreakpoint = function () {
        remountAll();
      };
      if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', onBreakpoint);
      } else if (typeof mobileQuery.addListener === 'function') {
        mobileQuery.addListener(onBreakpoint);
      }
    }

    document.addEventListener('shopify:section:load', function (event) {
      var stickyInScope = qs('[data-pdp-sticky-atc]', event.target);
      var heroInScope = event.target.matches('[data-pdp-hero], .pdp-hero') ||
        !!qs('[data-pdp-hero], .pdp-hero', event.target);

      if (stickyInScope) destroyController(stickyInScope);
      if (heroInScope) {
        var sticky = getSticky();
        if (sticky) destroyController(sticky);
      }

      remountAll();
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var sticky = qs('[data-pdp-sticky-atc]', event.target);
      if (sticky) destroyController(sticky);
      remountAll();
    });

    document.addEventListener('shopify:section:reorder', remountAll);

    if (isDesignMode()) {
      document.addEventListener('shopify:section:select', remountAll);
    }
  }

  function boot() {
    if (booted) {
      remountAll();
      return;
    }
    booted = true;
    bindGlobalEvents();
    mountWhenReady(0);

    window.addEventListener('load', function () {
      mountWhenReady(0);
    }, { once: true });

    if (isDesignMode()) {
      window.setTimeout(remountAll, 400);
      window.setTimeout(remountAll, 1200);
    }
  }

  window.PdpStickyAtc = {
    init: remountAll,
    refresh: refreshStickyAtc,
    teardown: destroyController,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
